import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { Logout } from '@mui/icons-material';
import DebugAuth from './DebugAuth';

const ProtectedRoute = ({ children, allowedUserTypes = [] }) => {
  const { isAuthenticated, user, loading, requiresProfileSetup, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect pharmacy users to setup if they need to complete their profile
  if (requiresProfileSetup && user.userType === 'pharmacy' && location.pathname !== '/pharmacy-setup') {
    return <Navigate to="/pharmacy-setup" replace />;
  }

  if (allowedUserTypes.length > 0 && !allowedUserTypes.includes(user.userType)) {
    const handleLogout = () => {
      logout();
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={3}
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" gutterBottom>
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Current user type:</strong> {user.userType}<br/>
            <strong>Required user type:</strong> {allowedUserTypes.join(' or ')}
          </Typography>
        </Alert>
        
        <Box textAlign="center">
          <Typography variant="body1" gutterBottom>
            To access this page, you need to log in with a {allowedUserTypes.join(' or ')} account.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Logout />}
              onClick={handleLogout}
            >
              Log Out and Switch Account
            </Button>
          </Box>
        </Box>
        
        {/* Debug component to help troubleshoot */}
        <DebugAuth />
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
